/**
 * OWL2 Class Expression Types (Re-exported from owl2 module)
 *
 * This module re-exports owl2 ClassExpression types with backward-compatible aliases
 * and provides domain-specific helper functions for class expression operations.
 *
 * Renamed from RestrictedObjectProperty.ts as part of the TBox/ABox refactoring.
 */
// =============================================================================
// Re-export Factory Functions with Aliases
// =============================================================================
import { owlClass, objectUnionOf, objectIntersectionOf, objectSomeValuesFrom, objectAllValuesFrom, objectHasValue, objectHasSelf, objectMinCardinality, objectMaxCardinality, objectExactCardinality, fullIRI, iriToString, isClass, isObjectUnionOf, isObjectIntersectionOf, isObjectSomeValuesFrom, isObjectAllValuesFrom, isObjectHasValue, isObjectHasSelf, isObjectMinCardinality, isObjectMaxCardinality, isObjectExactCardinality, isObjectPropertyRestriction as owl2IsObjectPropertyRestriction, isObjectProperty, getLocalName as owl2GetLocalName } from '@semantical/core/modules/owl2';
import { isNamedIndividual } from '@semantical/core/modules/owl2';
// Alias factory functions for backward compatibility
export const namedClass = (uri, _label) => owlClass(fullIRI(uri));
export const unionOf = (first, second, ...rest) => objectUnionOf(first, second, ...rest);
export const intersectionOf = (first, second, ...rest) => objectIntersectionOf(first, second, ...rest);
// Helper to create property expression from URI string
const propertyFromUri = (uri) => ({
    type: 'ObjectProperty',
    iri: fullIRI(uri)
});
export const someValuesFrom = (onProperty, filler) => objectSomeValuesFrom(propertyFromUri(onProperty), filler);
export const allValuesFrom = (onProperty, filler) => objectAllValuesFrom(propertyFromUri(onProperty), filler);
export const hasValue = (onProperty, individualUri) => objectHasValue(propertyFromUri(onProperty), { type: 'NamedIndividual', iri: fullIRI(individualUri) });
export const hasSelf = (onProperty) => objectHasSelf(propertyFromUri(onProperty));
export const minCardinality = (onProperty, cardinality, filler) => objectMinCardinality(cardinality, propertyFromUri(onProperty), filler);
export const maxCardinality = (onProperty, cardinality, filler) => objectMaxCardinality(cardinality, propertyFromUri(onProperty), filler);
export const exactCardinality = (onProperty, cardinality, filler) => objectExactCardinality(cardinality, propertyFromUri(onProperty), filler);
// =============================================================================
// Re-export Type Guards with Aliases
// =============================================================================
export const isNamedClass = isClass;
export const isUnionOf = isObjectUnionOf;
export const isIntersectionOf = isObjectIntersectionOf;
// Re-export IRI utilities
export { iriToString, fullIRI } from '@semantical/core/modules/owl2';
export const isSomeValuesFrom = isObjectSomeValuesFrom;
export const isAllValuesFrom = isObjectAllValuesFrom;
export { isObjectHasValue as isHasValue };
export { isObjectHasSelf as isHasSelf };
export { isObjectMinCardinality as isMinCardinality };
export { isObjectMaxCardinality as isMaxCardinality };
export { isObjectExactCardinality as isExactCardinality };
export { owl2IsObjectPropertyRestriction as isObjectPropertyRestriction };
// =============================================================================
// Helper Functions
// =============================================================================
/**
 * Get property IRI string from ObjectPropertyExpression
 */
function getPropertyUri(prop) {
    if (isObjectProperty(prop)) {
        return iriToString(prop.iri);
    }
    // For ObjectInverseOf, return the inner property IRI
    return iriToString(prop.property.iri);
}
/**
 * Extract all named class URIs from a class expression (recursive)
 * Includes URIs from fillers and onClass qualifiers in restrictions
 */
export function getNamedClassUris(expr) {
    switch (expr.type) {
        case 'Class':
            return [iriToString(expr.iri)];
        case 'ObjectUnionOf':
        case 'ObjectIntersectionOf':
            return expr.classExpressions.flatMap(getNamedClassUris);
        case 'ObjectSomeValuesFrom':
        case 'ObjectAllValuesFrom':
            return getNamedClassUris(expr.filler);
        case 'ObjectHasValue':
        case 'ObjectHasSelf':
            return [];
        case 'ObjectMinCardinality':
        case 'ObjectMaxCardinality':
        case 'ObjectExactCardinality':
            return expr.filler ? getNamedClassUris(expr.filler) : [];
        default:
            return [];
    }
}
/**
 * Check if a class URI satisfies a class expression.
 * For union: class must match ANY operand (OR)
 * For intersection: class must match ALL operands (AND)
 * For restrictions: class is considered to satisfy if it's a valid instance (always true at type level)
 *
 * @param classUri - The class URI to check
 * @param expression - The class expression constraint
 * @param isSubclassOf - Optional function to check subclass relationships
 */
export function classExpressionSatisfied(classUri, expression, isSubclassOf) {
    const matches = (uri, target) => isSubclassOf ? isSubclassOf(uri, target) : uri === target;
    switch (expression.type) {
        case 'Class':
            return matches(classUri, iriToString(expression.iri));
        case 'ObjectUnionOf':
            return expression.classExpressions.some(op => classExpressionSatisfied(classUri, op, isSubclassOf));
        case 'ObjectIntersectionOf':
            return expression.classExpressions.every(op => classExpressionSatisfied(classUri, op, isSubclassOf));
        // Property restrictions define constraints on instances, not class membership
        // At the class expression level, they're always considered satisfiable
        case 'ObjectSomeValuesFrom':
        case 'ObjectAllValuesFrom':
        case 'ObjectHasValue':
        case 'ObjectHasSelf':
        case 'ObjectMinCardinality':
        case 'ObjectMaxCardinality':
        case 'ObjectExactCardinality':
        case 'ObjectComplementOf':
        case 'ObjectOneOf':
        case 'DataSomeValuesFrom':
        case 'DataAllValuesFrom':
        case 'DataHasValue':
        case 'DataMinCardinality':
        case 'DataMaxCardinality':
        case 'DataExactCardinality':
            return true;
    }
}
/**
 * Helper to extract local name from a URI
 */
function getLocalName(uri) {
    return owl2GetLocalName(fullIRI(uri));
}
/**
 * Computes aggregated RestrictedObjectProperty[] from raw subClassOf axioms.
 * Filters ObjectPropertyRestriction types, groups by onProperty, and aggregates cardinalities.
 *
 * @param owlClass - Object with subClassOf axioms
 * @param propertyLabelResolver - Optional function to resolve property URI to label
 */
export function computeRestrictedObjectProperties(owlClass, propertyLabelResolver) {
    const subClassOf = owlClass.subClassOf || [];
    // Filter to only ObjectPropertyRestriction types
    const restrictions = subClassOf.filter(owl2IsObjectPropertyRestriction);
    // Group by property URI
    const groupedByProperty = new Map();
    for (const restriction of restrictions) {
        const propertyUri = getPropertyUri(restriction.property);
        const existing = groupedByProperty.get(propertyUri) || [];
        existing.push(restriction);
        groupedByProperty.set(propertyUri, existing);
    }
    // Aggregate each group into a RestrictedObjectProperty
    const result = [];
    for (const [propertyUri, propertyRestrictions] of groupedByProperty) {
        let minCardinality = 0;
        let maxCardinality = '*';
        let filler;
        let hasValueUri;
        let isUniversal = false;
        for (const restriction of propertyRestrictions) {
            switch (restriction.type) {
                case 'ObjectSomeValuesFrom':
                    // Existential: at least one value from filler
                    if (minCardinality < 1)
                        minCardinality = 1;
                    filler = restriction.filler;
                    break;
                case 'ObjectAllValuesFrom':
                    // Universal: all values must be from filler
                    filler = restriction.filler;
                    isUniversal = true;
                    break;
                case 'ObjectHasValue':
                    // Has specific value - implies min 1
                    if (minCardinality < 1)
                        minCardinality = 1;
                    if (isNamedIndividual(restriction.individual)) {
                        hasValueUri = iriToString(restriction.individual.iri);
                    }
                    break;
                case 'ObjectHasSelf':
                    // Self-reference - implies min 1
                    if (minCardinality < 1)
                        minCardinality = 1;
                    break;
                case 'ObjectMinCardinality':
                    if (restriction.cardinality > minCardinality) {
                        minCardinality = restriction.cardinality;
                    }
                    if (restriction.filler)
                        filler = restriction.filler;
                    break;
                case 'ObjectMaxCardinality':
                    if (maxCardinality === '*' || restriction.cardinality < maxCardinality) {
                        maxCardinality = restriction.cardinality;
                    }
                    if (restriction.filler)
                        filler = restriction.filler;
                    break;
                case 'ObjectExactCardinality':
                    // Exact implies both min and max
                    if (restriction.cardinality > minCardinality) {
                        minCardinality = restriction.cardinality;
                    }
                    if (maxCardinality === '*' || restriction.cardinality < maxCardinality) {
                        maxCardinality = restriction.cardinality;
                    }
                    if (restriction.filler)
                        filler = restriction.filler;
                    break;
            }
        }
        // Extract target class info from filler if it's a NamedClass
        let targetClassUri = '';
        let targetClassLabel = '';
        if (filler && isClass(filler)) {
            targetClassUri = iriToString(filler.iri);
            targetClassLabel = getLocalName(targetClassUri);
        }
        // Extract local name from property URI
        const localName = getLocalName(propertyUri);
        const resolvedLabel = propertyLabelResolver?.(propertyUri);
        result.push({
            onProperty: propertyUri,
            propertyLabel: resolvedLabel,
            filler,
            cardinality: { min: minCardinality, max: maxCardinality },
            hasValue: hasValueUri,
            hasValueLabel: hasValueUri ? getLocalName(hasValueUri) : undefined,
            isUniversal: isUniversal || undefined,
            sourceAxioms: propertyRestrictions,
            // Backward-compatible aliases
            uri: propertyUri,
            label: resolvedLabel || localName,
            localName,
            targetClassUri,
            targetClassLabel
        });
    }
    return result;
}
export function classExpressionToJSON(expr) {
    switch (expr.type) {
        case 'Class':
            return { type: 'Class', iri: iriToString(expr.iri) };
        case 'ObjectUnionOf':
            return {
                type: 'ObjectUnionOf',
                classExpressions: expr.classExpressions.map(classExpressionToJSON)
            };
        case 'ObjectIntersectionOf':
            return {
                type: 'ObjectIntersectionOf',
                classExpressions: expr.classExpressions.map(classExpressionToJSON)
            };
        case 'ObjectSomeValuesFrom':
            return {
                type: 'ObjectSomeValuesFrom',
                property: getPropertyUri(expr.property),
                filler: classExpressionToJSON(expr.filler)
            };
        case 'ObjectAllValuesFrom':
            return {
                type: 'ObjectAllValuesFrom',
                property: getPropertyUri(expr.property),
                filler: classExpressionToJSON(expr.filler)
            };
        case 'ObjectHasValue':
            return {
                type: 'ObjectHasValue',
                property: getPropertyUri(expr.property),
                individual: isNamedIndividual(expr.individual) ? iriToString(expr.individual.iri) : expr.individual.nodeId
            };
        case 'ObjectHasSelf':
            return {
                type: 'ObjectHasSelf',
                property: getPropertyUri(expr.property)
            };
        case 'ObjectMinCardinality':
            return {
                type: 'ObjectMinCardinality',
                property: getPropertyUri(expr.property),
                cardinality: expr.cardinality,
                ...(expr.filler && { filler: classExpressionToJSON(expr.filler) })
            };
        case 'ObjectMaxCardinality':
            return {
                type: 'ObjectMaxCardinality',
                property: getPropertyUri(expr.property),
                cardinality: expr.cardinality,
                ...(expr.filler && { filler: classExpressionToJSON(expr.filler) })
            };
        case 'ObjectExactCardinality':
            return {
                type: 'ObjectExactCardinality',
                property: getPropertyUri(expr.property),
                cardinality: expr.cardinality,
                ...(expr.filler && { filler: classExpressionToJSON(expr.filler) })
            };
        default:
            // For other types, return a basic representation
            return { type: expr.type };
    }
}
export function classExpressionFromJSON(json) {
    switch (json.type) {
        case 'Class':
            return owlClass(fullIRI(json.iri));
        case 'ObjectUnionOf':
            return objectUnionOf(classExpressionFromJSON(json.classExpressions[0]), classExpressionFromJSON(json.classExpressions[1]), ...json.classExpressions.slice(2).map(classExpressionFromJSON));
        case 'ObjectIntersectionOf':
            return objectIntersectionOf(classExpressionFromJSON(json.classExpressions[0]), classExpressionFromJSON(json.classExpressions[1]), ...json.classExpressions.slice(2).map(classExpressionFromJSON));
        case 'ObjectSomeValuesFrom':
            return objectSomeValuesFrom(propertyFromUri(json.property), classExpressionFromJSON(json.filler));
        case 'ObjectAllValuesFrom':
            return objectAllValuesFrom(propertyFromUri(json.property), classExpressionFromJSON(json.filler));
        case 'ObjectHasValue':
            return objectHasValue(propertyFromUri(json.property), { type: 'NamedIndividual', iri: fullIRI(json.individual) });
        case 'ObjectHasSelf':
            return objectHasSelf(propertyFromUri(json.property));
        case 'ObjectMinCardinality':
            return objectMinCardinality(json.cardinality, propertyFromUri(json.property), json.filler ? classExpressionFromJSON(json.filler) : undefined);
        case 'ObjectMaxCardinality':
            return objectMaxCardinality(json.cardinality, propertyFromUri(json.property), json.filler ? classExpressionFromJSON(json.filler) : undefined);
        case 'ObjectExactCardinality':
            return objectExactCardinality(json.cardinality, propertyFromUri(json.property), json.filler ? classExpressionFromJSON(json.filler) : undefined);
        default:
            throw new Error(`Unknown class expression type: ${json.type}`);
    }
}
